import { useEffect, useId, useRef, useState } from "react";
import type { FocusEvent, PointerEvent as ReactPointerEvent } from "react";
import type { CardDef, CardInstance, PlacedCard } from "../types";
import { RESOURCES, resourceMeta, setLabel } from "../gameMeta";
import { OFFICIAL_CATALOG_LOCALIZATION_IDS } from "../officialCatalogCards";
import { CardExplanation } from "./CardExplanation";
import { OfficialArtwork } from "./OfficialArtwork";
import { ResourceBadge } from "./ResourceIcon";

interface Props {
  instance?: CardInstance;
  placed?: PlacedCard;
  definition?: CardDef;
  hidden?: boolean;
  compact?: boolean;
  selected?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  footer?: React.ReactNode;
  explanation?: boolean;
}

export function GameCard(props: Props) {
  const { instance, placed, definition, hidden, compact } = props;
  const card = placed?.instance || instance;
  if (hidden || !card || !definition) {
    return (
      <div className={`game-card card-back ${compact ? "compact" : ""}`} aria-label="Verborgen kaart">
        <span className="back-crown">♛</span>
        <span>VORSTEN</span>
      </div>
    );
  }
  return <VisibleGameCard {...props} definition={definition} />;
}

function VisibleGameCard({ placed, definition, compact, selected, disabled, onClick, footer, explanation = true }: Props & { definition: CardDef }) {
  const cardRef = useRef<HTMLElement>(null);
  const [pointerInside, setPointerInside] = useState(false);
  const [keyboardFocusInside, setKeyboardFocusInside] = useState(false);
  const pointerFocus = useRef(false);
  const pointerResetTimer = useRef<number | undefined>(undefined);
  const generatedId = useId();
  const explanationId = `card-explanation-${generatedId.replace(/:/g, "")}`;
  const explanationOpen = explanation && (pointerInside || keyboardFocusInside);

  useEffect(() => () => window.clearTimeout(pointerResetTimer.current), []);

  const handlePointerEnter = (event: ReactPointerEvent<HTMLElement>) => {
    if (explanation && event.pointerType !== "touch") setPointerInside(true);
  };
  const handlePointerDown = () => {
    pointerFocus.current = true;
    setKeyboardFocusInside(false);
    window.clearTimeout(pointerResetTimer.current);
    pointerResetTimer.current = window.setTimeout(() => {
      pointerFocus.current = false;
    }, 0);
  };
  const handleFocus = () => {
    if (explanation && !pointerFocus.current) setKeyboardFocusInside(true);
  };
  const handleBlur = (event: FocusEvent<HTMLElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setKeyboardFocusInside(false);
  };

  const stats = definition.stats;
  return (
    <>
      <article
        ref={cardRef}
        className={`game-card set-${definition.set} kind-${definition.kind} ${compact ? "compact" : ""} ${selected ? "selected" : ""} ${onClick ? "interactive" : ""}`}
        onClick={disabled ? undefined : onClick}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={() => setPointerInside(false)}
        onPointerDownCapture={handlePointerDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        aria-label={`${definition.name}, ${setLabel(definition.set)}`}
        aria-describedby={explanationOpen ? explanationId : undefined}
        aria-disabled={disabled || undefined}
        aria-pressed={onClick && !footer ? selected : undefined}
        role={onClick && !footer ? "button" : undefined}
        tabIndex={explanation ? 0 : onClick && !disabled ? 0 : undefined}
        data-card-explanation-trigger={explanation ? definition.id : undefined}
        onKeyDown={(event) => {
          if (event.currentTarget !== event.target) return;
          if (onClick && !disabled && (event.key === "Enter" || event.key === " ")) {
            event.preventDefault();
            onClick();
          }
        }}
      >
        <div className="card-fallback">
          <header className="card-heading">
            <span className="card-set-mark">{definition.kind === "actie" ? "⚡" : definition.kind === "gebeurtenis" ? "?" : "♜"}</span>
            <strong>{definition.name}</strong>
          </header>
          <div className="card-art" aria-hidden="true">
            <span>{artSymbol(definition)}</span>
          </div>
          {!compact ? (
            <>
              <p className="card-text">{definition.text}</p>
            </>
          ) : null}
          <div className="card-stats">
            {stats.victory ? <span title="Zegepunten">♛ {stats.victory}</span> : null}
            {stats.strength ? <span title="Kracht">⚔ {stats.strength}</span> : null}
            {stats.commerce ? <span title="Handel">⚖ {stats.commerce}</span> : null}
            {stats.skill ? <span title="Vaardigheid">✧ {stats.skill}</span> : null}
            {stats.progress ? <span title="Vooruitgang">⌘ {stats.progress}</span> : null}
          </div>
        </div>
        <OfficialArtwork assetKey={`cards/${definition.id}`} className="official-card-art" />
        {OFFICIAL_CATALOG_LOCALIZATION_IDS.has(definition.id) ? (
          <div className="official-card-localization" data-official-card-localization={definition.id} aria-hidden="true">
            <strong>{definition.name}</strong>
            {!compact ? <span>{definition.text}</span> : null}
          </div>
        ) : null}
        {!compact && definition.cost.some(Boolean) ? (
          <div className="card-cost" aria-label={`Bouwkosten: ${definition.cost.flatMap((amount, index) => amount ? [`${amount} ${resourceMeta[RESOURCES[index]].label}`] : []).join(", ")}`}>
            {definition.cost.map((amount, index) => amount ? (
              <ResourceBadge key={RESOURCES[index]} resource={RESOURCES[index]} amount={amount} compact />
            ) : null)}
          </div>
        ) : null}
        {placed?.counter ? <b className="card-counter">{placed.counter}</b> : null}
        {footer ? <footer className="card-footer">{footer}</footer> : null}
      </article>
      {explanationOpen ? (
        <CardExplanation anchorRef={cardRef} definition={definition} id={explanationId} />
      ) : null}
    </>
  );
}

function artSymbol(definition: CardDef): string {
  if (definition.unit_kind === "held") return "⚔";
  if (definition.unit_kind === "handelsschip") return "⛵";
  if (definition.kind === "actie") return "✦";
  if (definition.kind === "landschapsuitbouw") return "⌂";
  if (definition.kind === "stadsgebouw") return "♜";
  if (definition.kind === "gebeurtenis") return "?";
  return "♝";
}
